/**
 * IntentResponseCard.jsx — V1 calm response surface.
 *
 * Operator doctrine (Mar 1, 2026):
 *
 *   No learner free-text input may immediately route to a
 *   dashboard. This component is the response surface that opens
 *   FIRST. It renders:
 *
 *     1. acknowledgement (heard-you sentence)
 *     2. summary (calm simplification)
 *     3. learner-chosen next steps (choices)
 *
 *   Navigation ONLY fires when the learner clicks a choice that
 *   carries a `route`. Choices without a route are inline
 *   continuation actions (open inline summary, ask clarifying
 *   question, etc.) — those are handed back to the parent via
 *   `onChoice` and the parent decides what to do.
 *
 *   This component does NOT call useNavigate, does NOT fetch
 *   data, and does NOT render dashboard / streak / stat widgets.
 *   It is intentionally small.
 */
import { useEffect, useRef } from "react";
import { X, MessageCircle, ArrowRight, HelpCircle } from "lucide-react";

const KIND_ICON = {
  navigate: ArrowRight,
  ask_clarify: HelpCircle,
  open_understanding: MessageCircle,
  open_inline: MessageCircle,
};

export default function IntentResponseCard({
  open,
  response,
  onChoice,
  onClose,
}) {
  const headingRef = useRef(null);

  // Move focus to the acknowledgement heading when the card opens
  // so screen-reader users hear it first (response-first doctrine
  // applies to assistive tech too).
  useEffect(() => {
    if (open && headingRef.current) {
      try { headingRef.current.focus({ preventScroll: false }); } catch { /* no-op */ }
    }
  }, [open]);

  if (!open || !response) return null;

  const choices = Array.isArray(response.choices) ? response.choices : [];

  return (
    <div
      data-testid="intent-response-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intent-response-card-heading"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6"
    >
      <div
        className="
          w-full max-w-md rounded-3xl bg-zinc-950 border border-zinc-800
          shadow-[0_0_40px_-12px_rgba(255,255,255,0.18)]
          p-6 sm:p-7 text-white relative
        "
      >
        {/* Close (calm escape — learner can always leave) */}
        <button
          type="button"
          data-testid="intent-response-card-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Acknowledgement (heard-you sentence) */}
        <h2
          ref={headingRef}
          id="intent-response-card-heading"
          tabIndex={-1}
          data-testid="intent-response-card-acknowledgement"
          className="text-xl sm:text-2xl font-extrabold leading-snug tracking-tight pr-8 outline-none"
        >
          {response.acknowledgement}
        </h2>

        {/* Calm simplification */}
        {response.summary ? (
          <p
            data-testid="intent-response-card-summary"
            className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed"
          >
            {response.summary}
          </p>
        ) : null}

        {/* Clarification badge — visible when category is unknown */}
        {response.requires_clarification ? (
          <p
            data-testid="intent-response-card-clarification-badge"
            className="mt-3 text-xs uppercase tracking-wider text-amber-400 font-bold"
          >
            Quick clarification
          </p>
        ) : null}

        {/* Learner choices — the ONLY way navigation can happen */}
        {choices.length > 0 ? (
          <ul
            data-testid="intent-response-card-choices"
            className="mt-5 space-y-2"
          >
            {choices.map((choice) => {
              const Icon = KIND_ICON[choice.kind] || ArrowRight;
              return (
                <li key={choice.id}>
                  <button
                    type="button"
                    data-testid={`intent-response-card-choice-${choice.id}`}
                    onClick={() => onChoice && onChoice(choice)}
                    className="
                      w-full flex items-center justify-between gap-3
                      text-left px-4 py-3 rounded-2xl
                      bg-zinc-900 border border-zinc-800
                      hover:bg-zinc-800 hover:border-zinc-700
                      transition-colors
                    "
                  >
                    <span className="text-sm sm:text-base font-semibold text-white">
                      {choice.label}
                    </span>
                    <Icon className="w-4 h-4 text-zinc-400 shrink-0" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {/* Doctrine marker — DOM pin used by source-pin doctrine
            tests to prove this surface exists and rendered. */}
        <span data-testid="intent-response-card-marker" hidden>
          response-first-v1·{response.category || "unknown"}
        </span>
      </div>
    </div>
  );
}
